import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  RefreshCw,
  FileSpreadsheet,
  ChevronRight,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import EmptyState from "../../components/system/EmptyState";

/**
 * Phase 2E S7 — Catalog Imports list for a given target tenant.
 *
 * Lives at ``/platform/companies/:cid/catalog/imports``. Operator-only
 * (gated by the parent route's ``PlatformProtectedRoute``).
 */
export default function CatalogImportsListPage() {
  const { cid } = useParams();
  const nav = useNavigate();

  const [company, setCompany] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [coRes, runsRes] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/catalog/imports`, {
          params: { limit: 100 },
        }),
      ]);
      setCompany(coRes.data.company || coRes.data);
      setRuns(runsRes.data.runs || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div data-testid="catalog-imports-loading" className="py-12 text-center">
        <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          Loading…
        </div>
      </div>
    );
  }
  if (!company) {
    return (
      <div data-testid="catalog-imports-error" className="py-12">
        <EmptyState
          icon={<FileSpreadsheet size={32} />}
          title="Tenant not found"
          description="The target company could not be loaded. Return to the company list."
          action={
            <Button tier="primary" onClick={() => nav("/platform")}>
              Back to tenants
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div data-testid="catalog-imports-page">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="catalog-imports-back"
        >
          <ArrowLeft size={14} />
          Back to {company.name}
        </Link>
      </div>

      <PageHeader
        eyebrow={
          <span className="font-mono text-[11px]">
            tenant: <span data-testid="catalog-imports-tenant-id">{cid}</span>
          </span>
        }
        title="Catalog imports"
        subtitle={
          <>
            Target company:{" "}
            <strong data-testid="catalog-imports-tenant-name">
              {company.name}
            </strong>
          </>
        }
        actions={
          <>
            <Button
              tier="secondary"
              onClick={load}
              data-testid="catalog-imports-refresh"
            >
              <RefreshCw size={14} /> Refresh
            </Button>
            <Button
              tier="primary"
              onClick={() =>
                nav(`/platform/companies/${cid}/catalog/imports/new`)
              }
              data-testid="catalog-imports-new"
            >
              <Plus size={16} /> New import
            </Button>
          </>
        }
      />

      {runs.length === 0 ? (
        <EmptyState
          icon={<FileSpreadsheet size={32} />}
          title="No catalog imports yet"
          description={`Start the first catalog import for ${company.name}. Imports create products in this tenant's catalog.`}
          action={
            <Button
              tier="primary"
              onClick={() =>
                nav(`/platform/companies/${cid}/catalog/imports/new`)
              }
              data-testid="catalog-imports-empty-cta"
            >
              <Plus size={16} /> Start first import
            </Button>
          }
          data-testid="catalog-imports-empty"
        />
      ) : (
        <Card variant="raised" className="overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.5fr_1fr_repeat(5,minmax(60px,1fr))_auto] gap-3 px-4 py-3 bg-paper-sunk border-b border-border/60 data-sku">
            <div>Run / Date</div>
            <div>Status</div>
            <div className="text-right">Rows</div>
            <div className="text-right">Add</div>
            <div className="text-right">Update</div>
            <div className="text-right">Invalid</div>
            <div className="text-right">Committed</div>
            <div className="w-6" />
          </div>
          {runs.map((r) => {
            const c = r.row_counts || {};
            return (
              <button
                key={r.id}
                onClick={() =>
                  nav(
                    `/platform/companies/${cid}/catalog/imports/${r.id}`,
                  )
                }
                className="w-full text-left grid grid-cols-[1.5fr_1fr_repeat(5,minmax(60px,1fr))_auto] gap-3 px-4 py-3 items-center border-t border-border/40 hover:bg-paper-2 transition-colors"
                data-testid={`catalog-import-row-${r.id}`}
              >
                <div>
                  <div className="font-mono text-[11px] text-muted-foreground truncate">
                    {r.id}
                  </div>
                  <div className="font-display text-xs text-ink">
                    {(r.submitted_at || "").slice(0, 19).replace("T", " ")}
                  </div>
                </div>
                <div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="text-right tabular-nums">{c.staged ?? 0}</div>
                <div className="text-right tabular-nums">{c.add ?? 0}</div>
                <div className="text-right tabular-nums">{c.update ?? 0}</div>
                <div className="text-right tabular-nums">
                  {(c.rejected ?? 0) + (c.needs_review ?? 0)}
                </div>
                <div className="text-right tabular-nums font-bold">
                  {c.committed ?? 0}
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </button>
            );
          })}
        </Card>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const color =
    status === "committed"
      ? "bg-emerald-100 text-emerald-900"
      : status === "review"
      ? "bg-amber-100 text-amber-900"
      : status === "cancelled"
      ? "bg-paper-sunk text-ink-2 line-through"
      : status === "failed"
      ? "bg-rose-100 text-rose-900"
      : "bg-paper-sunk text-ink-2";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-button font-mono text-[10px] uppercase tracking-wider ${color}`}
      data-testid={`catalog-import-status-${status}`}
    >
      {status || "—"}
    </span>
  );
}
